/**
 * La presentación de Newbery 3563 (Mati, 2026-11-03): textos, renders y plantas sacados de la carpeta comercial
 * (frontend/src/assets/newbery-3563/, recortados de las páginas del PDF sin los rótulos). La carpeta entera se
 * descarga desde la portada, apaisada (para la compu) o vertical (para el celular). Para actualizarla: reemplazar los
 * PDF y las imágenes con el mismo nombre.
 */
import acceso from "@/assets/newbery-3563/acceso.webp";
import balconParrilla from "@/assets/newbery-3563/balcon-parrilla.webp";
import balcones from "@/assets/newbery-3563/balcones.webp";
import boceto from "@/assets/newbery-3563/boceto.webp";
import carpeta from "@/assets/newbery-3563/newbery-3563-carpeta-comercial.pdf?url";
import carpetaMovil from "@/assets/newbery-3563/newbery-3563-carpeta-comercial-movil.pdf?url";
import cocheras from "@/assets/newbery-3563/cocheras.webp";
import cocinaContrafrente from "@/assets/newbery-3563/cocina-contrafrente.webp";
import dormitorio from "@/assets/newbery-3563/dormitorio.webp";
import duplex from "@/assets/newbery-3563/duplex.webp";
import estarContrafrente from "@/assets/newbery-3563/estar-contrafrente.webp";
import fachada from "@/assets/newbery-3563/fachada.webp";
import livingFrente from "@/assets/newbery-3563/living-frente.webp";
import mapa from "@/assets/newbery-3563/mapa.webp";
import piso6Cocina from "@/assets/newbery-3563/piso6-cocina.webp";
import planta135 from "@/assets/newbery-3563/planta-1-3-5.webp";
import planta24 from "@/assets/newbery-3563/planta-2-4.webp";
import planta6 from "@/assets/newbery-3563/planta-6.webp";
import planta7 from "@/assets/newbery-3563/planta-7.webp";
import planta8 from "@/assets/newbery-3563/planta-8.webp";
import plantaPb from "@/assets/newbery-3563/planta-pb.webp";

export interface Espacio {
  src: string;
  donde: string;
  titulo: string;
}

export interface Planta {
  clave: string;
  etiqueta: string;
  titulo: string;
  bajada: string;
  src: string;
  /** Unidad, cubierto, descubierto y total (m²). Vacío en planta baja. */
  superficies: {
    unidad: string;
    cubierto: string;
    descubierto: string;
    total: string;
  }[];
}

export const NEWBERY_3563 = {
  carpeta: {
    url: carpeta,
    archivo: "Newbery 3563 - Carpeta comercial.pdf",
    peso: "6,6 MB",
  },
  carpetaMovil: {
    url: carpetaMovil,
    archivo: "Newbery 3563 - Carpeta comercial (celular).pdf",
    peso: "5,2 MB",
  },
  portada: {
    zona: "Chacarita · Buenos Aires",
    nombre: "Newbery",
    numero: "3563",
    bajada: "Departamentos con balcón, de 65 a 135 m².",
    imagen: fachada,
    creditos: [
      { rol: "Desarrolla", quien: "2M Desarrollos" },
      { rol: "Proyecto", quien: "MIG Arquitectura" },
    ],
  },
  frase: {
    imagen: balcones,
    antes: "Vivir entre ",
    destacado: "curvas",
    despues: " y luz.",
  },
  edificio: {
    titulo: "Una fachada",
    destacado: "que se mueve",
    texto:
      "Newbery 3563 es un edificio de planta baja y ocho pisos en el corazón de Chacarita. Balcones curvos de hormigón, barandas de hierro y verde en cada nivel. Todas las unidades, de 65 a 135 m², tienen balcón o terraza con parrilla propia.",
    boceto,
  },
  numeros: [
    { cifra: "PB+8", texto: "Planta baja y ocho pisos" },
    { cifra: "12", texto: "Unidades" },
    { cifra: "65–135", texto: "m² totales por unidad" },
    { cifra: "100%", texto: "Con balcón o terraza y parrilla propia" },
    { cifra: "2", texto: "Unidades por piso, del 1º al 5º" },
    { cifra: "PB", texto: "Cocheras" },
  ],
  espacios: [
    {
      src: livingFrente,
      donde: "Pisos 1º a 5º · Frente",
      titulo: "Living comedor abierto al balcón",
    },
    {
      src: balconParrilla,
      donde: "Pisos 1º a 5º · Frente",
      titulo: "Balcón semicubierto con parrilla",
    },
    {
      src: cocinaContrafrente,
      donde: "Pisos 1º a 5º · Contrafrente",
      titulo: "Cocina integrada al estar",
    },
    {
      src: estarContrafrente,
      donde: "Pisos 1º a 5º · Contrafrente",
      titulo: "Estar con salida al balcón",
    },
    {
      src: dormitorio,
      donde: "Pisos 1º a 5º",
      titulo: "Dormitorio con salida al balcón",
    },
    {
      src: piso6Cocina,
      donde: "Piso 6º · 135 m²",
      titulo: "Cocina abierta a 40 m² de terraza",
    },
    {
      src: duplex,
      donde: "Piso 7º · Dúplex",
      titulo: "Escalera a la terraza exclusiva del piso 8",
    },
    {
      src: acceso,
      donde: "Planta baja",
      titulo: "Acceso sobre Av. Jorge Newbery",
    },
    {
      src: cocheras,
      donde: "Planta baja",
      titulo: "Cocheras en un patio verde",
    },
  ] satisfies Espacio[],
  barrio: {
    titulo: "El barrio del que",
    destacado: "todos hablan",
    prensa: [
      {
        cifra: "#34",
        texto: "Entre los 51 barrios más cool del mundo, según la revista Time Out.",
        fuente: "La Nación · 2022",
      },
      {
        cifra: "Foco",
        texto: "Uno de los cuatro barrios porteños donde los desarrolladores ponen el foco, por su cercanía a Palermo y Colegiales.",
        fuente: "Forbes Argentina · 2026",
      },
      {
        cifra: "−20%",
        texto: "El barrio que le pisa los talones a Palermo: el m² cuesta entre 15% y 20% menos.",
        fuente: "Infobae · 2026",
      },
    ],
  },
  ubicacion: {
    calle: "Av. Jorge Newbery 3563",
    barrio: "Chacarita",
    mapa,
    conectividad: {
      texto: "Ubicación central, a minutos de las principales avenidas.",
      avenidas: [
        { nombre: "Av. Córdoba", hacia: "al centro", distancia: "1 cuadra" },
        { nombre: "Av. Álvarez Thomas", hacia: "a Villa Urquiza y Saavedra", distancia: "2 cuadras" },
        { nombre: "Av. Federico Lacroze", hacia: "a Colegiales y Belgrano", distancia: "3 cuadras" },
        { nombre: "Av. Dorrego", hacia: "a Palermo", distancia: "4 cuadras" },
        { nombre: "Av. Corrientes", hacia: "directo al Obelisco", distancia: "6 cuadras" },
      ],
    },
    transporte: [
      { siglas: [] as string[], nombre: "33 paradas de colectivo", detalle: "a menos de 400 m" },
      { siglas: ["B", "U"], nombre: "Subte B y tren Urquiza", detalle: "Estación Federico Lacroze, a 9 cuadras" },
    ],
    verdes: [
      { nombre: "Plaza Mafalda", detalle: "a 6 cuadras" },
      { nombre: "Plaza Clemente", detalle: "a 7 cuadras" },
    ],
    educacion: "Más de 20 escuelas y jardines a menos de 10 minutos a pie.",
    nota: "Distancias aproximadas, medidas sobre el mapa. Datos: © colaboradores de OpenStreetMap.",
  },
  mesa: {
    titulo: "La mesa,",
    destacado: "a la vuelta",
    texto: "Más de 50 restaurantes, cafés y bares a menos de 600 m. Algunos de los más cercanos:",
    lugares: [
      { nombre: "Silvestre", tipo: "café", minutos: 1 },
      { nombre: "Ajo Negro", tipo: "restaurante", minutos: 2 },
      { nombre: "Lardito", tipo: "restaurante", minutos: 2 },
      { nombre: "Obrador Las Damas", tipo: "restaurante", minutos: 3 },
      { nombre: "Salgado", tipo: "restaurante", minutos: 3 },
      { nombre: "El Golfo", tipo: "restaurante", minutos: 4 },
      { nombre: "Lutero Bar", tipo: "bar", minutos: 5 },
    ],
  },
  plantas: [
    {
      clave: "pb",
      etiqueta: "PB",
      titulo: "Planta baja",
      bajada: "Acceso, hall y cocheras.",
      src: plantaPb,
      superficies: [],
    },
    {
      clave: "135",
      etiqueta: "1º, 3º y 5º",
      titulo: "Pisos 1º, 3º y 5º",
      bajada: "Dos unidades por piso, al frente y al contrafrente, cada una con su balcón y parrilla.",
      src: planta135,
      superficies: [
        {
          unidad: "Frente",
          cubierto: "49 m²",
          descubierto: "17 m²",
          total: "66 m²",
        },
        {
          unidad: "Contrafrente",
          cubierto: "49 m²",
          descubierto: "16 m²",
          total: "65 m²",
        },
      ],
    },
    {
      clave: "24",
      etiqueta: "2º y 4º",
      titulo: "Pisos 2º y 4º",
      bajada: "Dos unidades por piso, al frente y al contrafrente, cada una con su balcón y parrilla.",
      src: planta24,
      superficies: [
        {
          unidad: "Frente",
          cubierto: "49 m²",
          descubierto: "17 m²",
          total: "66 m²",
        },
        {
          unidad: "Contrafrente",
          cubierto: "49 m²",
          descubierto: "16 m²",
          total: "65 m²",
        },
      ],
    },
    {
      clave: "6",
      etiqueta: "6º",
      titulo: "Piso 6º",
      bajada: "Una unidad por piso, con 40 m² de terraza al frente.",
      src: planta6,
      superficies: [
        {
          unidad: "Piso 6º",
          cubierto: "95 m²",
          descubierto: "40 m²",
          total: "135 m²",
        },
      ],
    },
    {
      clave: "7",
      etiqueta: "7º dúplex",
      titulo: "Piso 7º · Dúplex",
      bajada: "Con escalera a la terraza exclusiva del piso 8.",
      src: planta7,
      superficies: [
        {
          unidad: "Piso 7º",
          cubierto: "77 m²",
          descubierto: "16 m²",
          total: "93 m²",
        },
      ],
    },
    {
      clave: "8",
      etiqueta: "8º terraza",
      titulo: "Piso 8º · Terraza",
      bajada: "Terraza con pileta y parrilla, exclusiva del dúplex del piso 7 (a confirmar).",
      src: planta8,
      superficies: [
        {
          unidad: "Terraza exclusiva",
          cubierto: "—",
          descubierto: "40 m²",
          total: "40 m²",
        },
      ],
    },
  ] satisfies Planta[],
  cierre: { titulo: "Tu lugar", destacado: "en Chacarita." },
  legal:
    "Imágenes ilustrativas, no contractuales. Mobiliario, equipamiento y vegetación no incluidos. Superficies aproximadas: las medidas de los planos pueden sufrir modificaciones por razones técnicas.",
};

export type PresentacionProyecto = typeof NEWBERY_3563;
